import React, { useState, useEffect, useContext } from 'react'
import { FormatContext } from '../context/FormatContext'
import '../style/traffic.css'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(iso) {
    if (!iso) return '—'
    return new Date(iso).toLocaleString('sv-SE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
}

const STATUS_LABEL = {
    clear: 'CLEAR',
    congested: 'CONGESTED',
    unknown: 'UNKNOWN',
    error: 'ERROR',
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Traffic() {
    const { theme } = useContext(FormatContext)
    const isDark = theme === 'dark'

    const [trafficData, setTrafficData] = useState(null)
    const [loadingStatus, setLoadingStatus] = useState(true)
    const [checking, setChecking] = useState(false)
    const [statusError, setStatusError] = useState(null)
    const [checkError, setCheckError] = useState(null)
    const [testPassword, setTestPassword] = useState('')
    const [sendingTest, setSendingTest] = useState(false)
    const [testResult, setTestResult] = useState(null) // { ok: bool, msg: string }

    useEffect(() => {
        console.log('Loading traffic status…')
        loadStatus()
    }, [])

    async function loadStatus() {
        console.log('[traffic] loadStatus: start')
        setLoadingStatus(true)
        setStatusError(null)
        try {
            console.log('[traffic] loadStatus: fetching /.netlify/functions/traffic-status')
            const res = await fetch('/.netlify/functions/traffic-status')
            console.log('[traffic] loadStatus: response status', res.status, res.statusText)
            const text = await res.text()
            console.log('[traffic] loadStatus: raw response body', text.slice(0, 500))
            let data
            try {
                data = JSON.parse(text)
            } catch (parseErr) {
                const msg = `Function returned non-JSON (HTTP ${res.status}): ${text.slice(0, 200)}`
                console.error('[traffic] loadStatus: JSON parse error', parseErr, msg)
                throw new Error(msg)
            }
            console.log('[traffic] loadStatus: parsed data', data)
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
            setTrafficData(data)
            console.log('[traffic] loadStatus: done')
        } catch (err) {
            console.error('[traffic] loadStatus: caught error', err)
            setStatusError(err.message || 'Could not load traffic status.')
        } finally {
            setLoadingStatus(false)
        }
    }

    async function checkNow() {
        console.log('[traffic] checkNow: start')
        setChecking(true)
        setCheckError(null)
        try {
            console.log('[traffic] checkNow: fetching /.netlify/functions/check-traffic')
            const res = await fetch('/.netlify/functions/check-traffic', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            })
            console.log('[traffic] checkNow: response status', res.status, res.statusText)
            const text = await res.text()
            console.log('[traffic] checkNow: raw response body', text.slice(0, 500))
            let data
            try {
                data = JSON.parse(text)
            } catch (parseErr) {
                const msg = `Function returned non-JSON (HTTP ${res.status}): ${text.slice(0, 200)}`
                console.error('[traffic] checkNow: JSON parse error', parseErr, msg)
                throw new Error(msg)
            }
            console.log('[traffic] checkNow: parsed data', data)
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
            if (data.error) throw new Error(data.error)
            setTrafficData(data)
            console.log('[traffic] checkNow: done')
        } catch (err) {
            console.error('[traffic] checkNow: caught error', err)
            setCheckError(err.message || 'Check failed.')
        } finally {
            setChecking(false)
        }
    }

    async function sendTestNotification(e) {
        e.preventDefault()
        setSendingTest(true)
        setTestResult(null)
        try {
            const res = await fetch('/.netlify/functions/test-notification', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password: testPassword }),
            })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
            setTestResult({ ok: true, msg: 'Notification sent!' })
            setTestPassword('')
        } catch (err) {
            setTestResult({ ok: false, msg: err.message })
        } finally {
            setSendingTest(false)
        }
    }

    const status = trafficData ? trafficData.status : null

    return (
        <div className={`traffic--page${isDark ? ' traffic--dark' : ''}`}>
            <h1 className="traffic--title">Traffic Monitor</h1>

            {trafficData && (
                <p className="traffic--subtitle">
                    {trafficData.streetName || '—'}
                    {trafficData.lat != null && trafficData.lon != null && (
                        <>
                            &nbsp;·&nbsp;
                            <span className="traffic--coords">{trafficData.lat}, {trafficData.lon}</span>
                        </>
                    )}
                </p>
            )}

            {loadingStatus && <p className="traffic--loading">Loading status…</p>}

            {!loadingStatus && statusError && (
                <p className="traffic--error--msg">{statusError}</p>
            )}

            {!loadingStatus && trafficData && (
                <>
                    {/* ── Status badge ── */}
                    <div className="traffic--card">
                        <div className={`traffic--badge traffic--badge--${status}`}>
                            {STATUS_LABEL[status] || (status ? status.toUpperCase() : 'UNKNOWN')}
                        </div>

                        {/* ── Speed stats ── */}
                        <div className="traffic--grid">
                            <div className="traffic--stat">
                                <span className="traffic--stat--label">Current speed</span>
                                <span className="traffic--stat--value">
                                    {trafficData.currentSpeed != null
                                        ? `${trafficData.currentSpeed} km/h`
                                        : '—'}
                                </span>
                            </div>
                            <div className="traffic--stat">
                                <span className="traffic--stat--label">Free-flow speed</span>
                                <span className="traffic--stat--value">
                                    {trafficData.freeFlowSpeed != null
                                        ? `${trafficData.freeFlowSpeed} km/h`
                                        : '—'}
                                </span>
                            </div>
                            <div className="traffic--stat">
                                <span className="traffic--stat--label">Speed ratio</span>
                                <span className="traffic--stat--value">
                                    {trafficData.speedRatio != null
                                        ? trafficData.speedRatio.toFixed(2)
                                        : '—'}
                                </span>
                            </div>
                        </div>

                        {/* ── Timestamps ── */}
                        <div className="traffic--timestamps">
                            <div className="traffic--ts">
                                <span className="traffic--ts--label">Last checked</span>
                                <span className="traffic--ts--value">
                                    {formatDateTime(trafficData.lastCheckedAt)}
                                </span>
                            </div>
                            {trafficData.lastNotificationAt && (
                                <div className="traffic--ts">
                                    <span className="traffic--ts--label">Last notification</span>
                                    <span className="traffic--ts--value">
                                        {formatDateTime(trafficData.lastNotificationAt)}
                                    </span>
                                </div>
                            )}
                            {trafficData.lastError && !trafficData.lastError.startsWith('storage:') && (
                                <div className="traffic--ts traffic--ts--error">
                                    <span className="traffic--ts--label">Last error</span>
                                    <span className="traffic--ts--value traffic--error--text">
                                        {trafficData.lastError}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Check now button ── */}
                    <button
                        className="traffic--check--btn"
                        onClick={checkNow}
                        disabled={checking}
                    >
                        {checking ? 'Checking…' : 'Check now'}
                    </button>

                    {checkError && <p className="traffic--error--msg">{checkError}</p>}

                    {/* ── Test notification ── */}
                    <form className="traffic--test--form" onSubmit={sendTestNotification}>
                        <input
                            type="text"
                            name="username"
                            autoComplete="username"
                            tabIndex="-1"
                            aria-hidden="true"
                            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                            readOnly
                            value="traffic-monitor"
                        />
                        <input
                            className="traffic--test--input"
                            type="password"
                            placeholder="Password"
                            value={testPassword}
                            onChange={e => { setTestPassword(e.target.value); setTestResult(null) }}
                            autoComplete="current-password"
                        />
                        <button
                            className="traffic--test--btn"
                            type="submit"
                            disabled={sendingTest || !testPassword}
                        >
                            {sendingTest ? 'Sending…' : 'Test notification'}
                        </button>
                    </form>
                    {testResult && (
                        <p className={testResult.ok ? 'traffic--test--ok' : 'traffic--error--msg'}>
                            {testResult.msg}
                        </p>
                    )}

                    {/* ── Threshold info ── */}
                    <div className={`traffic--info${isDark ? ' traffic--info--dark' : ''}`}>
                        <h3 className="traffic--info--heading">Thresholds</h3>
                        <ul className="traffic--info--list">
                            <li>
                                <strong>Congested</strong> when speed ratio &lt; 0.55
                            </li>
                            <li>
                                <strong>Resolved</strong> when speed ratio &gt; 0.80 (after congestion)
                            </li>
                        </ul>
                        <p className="traffic--schedule--note">
                            The automatic check runs every 5&nbsp;minutes on weekdays
                            15:00–19:00 Stockholm time. Outside that window the scheduled
                            function skips the API call.
                        </p>
                    </div>
                </>
            )}

            {!loadingStatus && !trafficData && !statusError && (
                <div>
                    <p className="traffic--loading">
                        No data saved yet. Press <strong>Check now</strong> to run the first check.
                    </p>
                    <button
                        className="traffic--check--btn"
                        onClick={checkNow}
                        disabled={checking}
                    >
                        {checking ? 'Checking…' : 'Check now'}
                    </button>
                    {checkError && <p className="traffic--error--msg">{checkError}</p>}
                </div>
            )}
        </div>
    )
}
